import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiBriefcase, FiShield } from 'react-icons/fi';
import { useAuthStore } from '../store/authStore';

const ViewSwitcher = () => {
  const navigate = useNavigate();
  const activeView = useAuthStore((state) => state.activeView);
  const lraRole = useAuthStore((state) => state.lraRole);
  const setActiveView = useAuthStore((state) => state.setActiveView);
  const hasLRAAccess = useAuthStore((state) => state.hasLRAAccess);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // ALL hooks must run on every render — before any early return
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // NOW we can conditionally return null (after all hooks have run)
  if (!hasLRAAccess()) return null;

  const handleSwitch = (view) => {
    setActiveView(view);
    setOpen(false);
    if (view === 'lra') {
      navigate('/lra/intelligence');
    } else {
      navigate('/dashboard');
    }
  };

  const roleLabel =
    lraRole === 'admin'
      ? 'LRA Admin'
      : lraRole === 'officer'
      ? 'LRA Officer'
      : lraRole === 'agent'
      ? 'LRA Agent'
      : 'LRA';

  const currentLabel = activeView === 'lra' ? roleLabel : 'Merchant';

  return (
    <div
      ref={dropdownRef}
      style={{ position: 'relative', marginRight: '12px' }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          background: activeView === 'lra' ? '#fef2f2' : '#eff6ff',
          border: `1px solid ${
            activeView === 'lra' ? '#fca5a5' : '#93c5fd'
          }`,
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
          color: activeView === 'lra' ? '#991b1b' : '#1e40af',
          transition: 'all 0.15s',
        }}
      >
        {activeView === 'lra' ? (
          <FiShield size={16} />
        ) : (
          <FiBriefcase size={16} />
        )}
        {currentLabel}
        <FiChevronDown size={14} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: '220px',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '8px 14px',
              fontSize: '11px',
              fontWeight: 700,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            Switch Portal
          </div>

          <SwitchOption
            active={activeView === 'merchant'}
            icon={<FiBriefcase size={16} />}
            label="Merchant Portal"
            description="Manage your own business"
            onClick={() => handleSwitch('merchant')}
          />

          <SwitchOption
            active={activeView === 'lra'}
            icon={<FiShield size={16} />}
            label={`${roleLabel} Portal`}
            description="National intelligence & audit"
            onClick={() => handleSwitch('lra')}
          />
        </div>
      )}
    </div>
  );
};

const SwitchOption = ({ active, icon, label, description, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '12px 14px',
      cursor: 'pointer',
      background: active ? '#f3f4f6' : '#fff',
      borderBottom: '1px solid #f9fafb',
      transition: 'background 0.15s',
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.background = '#f9fafb';
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.background = '#fff';
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '2px',
      }}
    >
      {icon}
      <span style={{ fontWeight: 600, fontSize: '14px' }}>{label}</span>
      {active && (
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            color: '#22c55e',
            fontWeight: 700,
          }}
        >
          ✓
        </span>
      )}
    </div>
    <div style={{ fontSize: '12px', color: '#6b7280', marginLeft: '26px' }}>
      {description}
    </div>
  </div>
);

export default ViewSwitcher;
